import { PanelLeftClose, PanelLeftOpen } from 'lucide-react'
import { useState } from 'react'

import { SidebarContent, SidebarProvider } from '@/components/ui/sidebar'
import { cn } from '@/lib/utils'
import { AccountContent } from './AccountContent'
import { DEFAULT_ORG_NAME, Viewport } from './accountModel'
import { AgentComingSoon } from './AgentComingSoon'
import { AgentNameProvider } from './agentName'
import { CfoOverview } from './CfoOverview'
import { ChatPane } from './ChatPane'
import { CFO_THREAD, Person } from './contacts'
import { FleetTopNav } from './FleetTopNav'
import { InsuranceBlankState } from './InsuranceBlankState'
import { NavRows } from './NavRows'
import { PinnedArtifactsProvider } from './pinnedArtifacts'
import { navGroups, ProductTab, TAB_ENTITY } from './prototypeNav'
import { TeamPanel } from './TeamPanel'

/** A margin note beside the agent, not a column competing with it. */
const TEAM_PANEL_WIDTH = 220

/** Room to read in, now the agent has the page rather than an edge of it. */
const AGENT_CHAT_WIDTH = 520

/** What the top bar is pointed at. The agent sits outside the products. */
enum View {
  AGENT = 'AGENT',
  PRODUCT = 'PRODUCT',
}

/** The agent insurance already has, before Cai takes the work over. */
const INSURE_BOT = 'InsureBot'

/** A product's first row, which is where opening it lands — null when it has none. */
function firstRow(tab: ProductTab): string | null {
  return navGroups(tab)[0]?.rows[0]?.title ?? null
}

/**
 * Option 3's desktop. Forked from Option 2's rather than shared with it, so the
 * two can be pulled apart without one dragging the other along.
 */
export function FleetDesktopPlacement({
  notificationId,
  className,
  plainAgent,
  mvp,
  notInsured,
  cfoOnly,
}: {
  notificationId: number
  className?: string
  /** The agent is the brand here, so its name is not repeated on the mark or the avatar. */
  plainAgent?: boolean
  /** Ships before the rest of it: the agent is flagged, and the rail is not live. */
  mvp?: boolean
  /** The fleet has no policy, so the Insurance tab sells rather than reports. */
  notInsured?: boolean
  /** CFO is the live capability here, so its page and its thread are the ones shown. */
  cfoOnly?: boolean
}) {
  const [tab, setTab] = useState<ProductTab>(ProductTab.INSURANCE)
  // No dashboard under this placement, so a product always opens on a row.
  const [page, setPage] = useState<string | null>(firstRow(ProductTab.INSURANCE))
  // Collapsed by default: under this placement the chat is the column that earns the room.
  const [navOpen, setNavOpen] = useState(false)
  // The agent's page is also where the account lands: with the dashboard gone,
  // there is nothing more general to open on.
  // No agent tab at MVP: it lives inside the products rather than beside them.
  const [view, setView] = useState<View>(mvp ? View.PRODUCT : View.AGENT)

  // CFO ships as a conversation rather than a set of screens.
  const agentIsThePage = mvp && tab === ProductTab.CFO
  // At MVP the chat is the agent alone; there is no one else to pull into it.
  const chatPeople = mvp ? [] : undefined

  function selectProduct(next: ProductTab) {
    setView(View.PRODUCT)
    setTab(next)
    setPage(firstRow(next))
  }

  return (
    <PinnedArtifactsProvider>
      {/* min-h-0 overrides the provider's min-h-svh so the pane keeps its own height. */}
      <SidebarProvider
        // Drives the provider's own collapsed state, which is what gates the row
        // tooltips — without it SidebarMenuButton reads "expanded" and hides them.
        open={navOpen}
        onOpenChange={setNavOpen}
        className={cn('border-ink-100 block min-h-0 overflow-hidden border bg-white', className)}
      >
        <div className="flex h-full flex-col">
          <FleetTopNav
            tab={tab}
            onSelect={selectProduct}
            orgName={DEFAULT_ORG_NAME}
            symbolOnlyLogo={plainAgent}
            betaTab={mvp ? ProductTab.CFO : undefined}
            agentActive={view === View.AGENT}
            onAgent={() => setView(View.AGENT)}
          />

          {view === View.AGENT ? (
            <div className="flex min-h-0 flex-1">
              {mvp ? (
                // The tab is here from the first release; what goes in it is not.
                <AgentComingSoon />
              ) : (
                <>
                  <ChatPane
                    notified={notificationId > 0}
                    width="fill"
                    contentWidth={AGENT_CHAT_WIDTH}
                    connect
                    hideAgentLabel={plainAgent}
                    agentThread={cfoOnly ? CFO_THREAD : undefined}
                  />
                  <TeamPanel width={TEAM_PANEL_WIDTH} />
                </>
              )}
            </div>
          ) : (
            <div className="flex min-h-0 flex-1">
              {/* CFO has no screens of its own, so there is nothing to navigate. */}
              {navGroups(tab).length > 0 && (
                <div
                  className={cn(
                    'border-ink-100 flex shrink-0 flex-col border-r',
                    navOpen ? 'w-40' : 'w-(--sidebar-width-icon)'
                  )}
                >
                  <SidebarContent className="no-scrollbar gap-0">
                    <NavRows tab={tab} page={page} onNavigate={setPage} collapsed={!navOpen} />
                    {navOpen && (
                      <p className="text-ink-300 mt-auto truncate px-4 py-3 text-[10px] leading-4">
                        {TAB_ENTITY[tab]}
                      </p>
                    )}
                  </SidebarContent>
                  <button
                    type="button"
                    onClick={() => setNavOpen((was) => !was)}
                    aria-label={navOpen ? 'Collapse navigation' : 'Expand navigation'}
                    aria-expanded={navOpen}
                    className="hover:bg-sidebar-accent mt-auto flex items-center justify-center py-2"
                  >
                    {navOpen ? (
                      <PanelLeftClose className="text-ink-300 size-4" />
                    ) : (
                      <PanelLeftOpen className="text-ink-300 size-4" />
                    )}
                  </button>
                </div>
              )}

              {/* CFO is the agent: no page to sit beside, so the chat takes the
                middle the way it does on its own tab. */}
              {agentIsThePage ? (
                <ChatPane
                  notified={notificationId > 0}
                  width="fill"
                  contentWidth={AGENT_CHAT_WIDTH}
                  connect
                  hideAgentLabel={plainAgent}
                  people={chatPeople}
                  agentThread={CFO_THREAD}
                />
              ) : (
                <>
                  <div className="min-w-0 flex-1 overflow-hidden">
                    {notInsured && tab === ProductTab.INSURANCE ? (
                      <InsuranceBlankState />
                    ) : cfoOnly && tab === ProductTab.CFO ? (
                      <CfoOverview />
                    ) : (
                      <AccountContent viewport={Viewport.DESKTOP} page={page} />
                    )}
                  </div>

                  {/* At MVP the agent only covers insurance; the books are their
                    own product, with no Cai beside them yet. */}
                  {mvp && tab === ProductTab.BOOKKEEPER ? null : notInsured &&
                    tab === ProductTab.INSURANCE ? (
                    // Insurance runs its own bot today, with no people but the
                    // broker, one surface, and no history.
                    <AgentNameProvider name={INSURE_BOT}>
                      <ChatPane
                        className="border-ink-100 border-l"
                        blankChat
                        hideTabBar
                        people={chatPeople ?? [Person.LUPITA, Person.CARRIE]}
                        agentTint="bg-blue-100"
                        banner={`${INSURE_BOT} will be replaced with Cai soon.`}
                      />
                    </AgentNameProvider>
                  ) : (
                    <ChatPane
                      notified={notificationId > 0}
                      className="border-ink-100 border-l"
                      connect
                      hideAgentLabel={plainAgent}
                      people={chatPeople}
                      agentThread={cfoOnly ? CFO_THREAD : undefined}
                    />
                  )}
                </>
              )}
            </div>
          )}
        </div>
      </SidebarProvider>
    </PinnedArtifactsProvider>
  )
}
